// Pestaña "Bandeja" de la pantalla del asistente: preguntas, avisos y resúmenes que el asistente le
// deja al dueño. Filtro por estado, cuerpo desplegable, "Marcar leído" y "Responder" (con opción de
// guardar la respuesta como directiva).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Loader2, ChevronDown, ChevronRight, Check, Reply } from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { Can } from '../../lib/rbac';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { AssistantMarkdown } from '../../components/AssistantMarkdown';
import { fmtDateTime, INBOX_KIND_LABEL, useSeesAllSellers, type InboxRow } from './shared';

type StatusFilter = 'unread' | 'read' | 'answered' | 'all';

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'unread', label: 'No leídos' },
  { key: 'read', label: 'Leídos' },
  { key: 'answered', label: 'Respondidos' },
  { key: 'all', label: 'Todos' },
];

const STATUS_TEXT: Record<string, { label: string; tone: 'ok' | 'warning' | 'info' }> = {
  unread: { label: 'No leído', tone: 'warning' },
  read: { label: 'Leído', tone: 'info' },
  answered: { label: 'Respondido', tone: 'ok' },
};

const LIMIT = 200;
const MAX_TEXT = 4000;

const inputCls =
  'px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded text-[var(--color-text)]';
const btnPrimary =
  'inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-40';
const btnGhost =
  'inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-40';

type Toast = { kind: 'ok' | 'err'; msg: string } | null;

export default function InboxTab({ onChanged }: { onChanged?: () => void }) {
  const sellerId = useUIStore((s) => s.sellerId);
  const sellers = useUIStore((s) => s.sellers);
  const seesAll = useSeesAllSellers();

  const [filter, setFilter] = useState<StatusFilter>('unread');
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState<Record<number, boolean>>({});
  const [toast, setToast] = useState<Toast>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqSeq = useRef(0);

  // Formulario de respuesta (uno abierto a la vez)
  const [replyId, setReplyId] = useState<number | null>(null);
  const [answer, setAnswer] = useState('');
  const [saveAsDirective, setSaveAsDirective] = useState(false);
  const [directiveScope, setDirectiveScope] = useState<'global' | 'seller'>('seller');

  const showToast = (t: Toast) => {
    setToast(t);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };
  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    []
  );

  const sellerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of sellers) map.set(s.id, s.nickname);
    return (id: number) => map.get(id) ?? String(id);
  }, [sellers]);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ seller_id: String(sellerId), status: filter, limit: String(LIMIT) });
      const res = await api.get<InboxRow[]>(`/assistant/inbox?${qs.toString()}`);
      if (seq !== reqSeq.current) return;
      if (res.success && res.data) {
        setRows(res.data);
        setError(null);
      } else {
        setError(res.error || 'No se pudo cargar la bandeja.');
      }
    } catch {
      if (seq === reqSeq.current) setError('No se pudo cargar la bandeja.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [sellerId, filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const openReply = (r: InboxRow) => {
    setReplyId(r.id);
    setAnswer('');
    setSaveAsDirective(false);
    // Alcance por defecto: el del aviso (global solo si es global y el usuario puede escribir globales).
    setDirectiveScope(r.seller_id === null && seesAll ? 'global' : 'seller');
    setExpanded((e) => ({ ...e, [r.id]: true }));
  };

  const send = async (r: InboxRow, body: Record<string, unknown>, okMsg: string) => {
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const res = await api.patch<{ directive_id?: number }>(`/assistant/inbox/${r.id}`, body);
      if (res.success) {
        showToast({ kind: 'ok', msg: okMsg });
        if (replyId === r.id) setReplyId(null);
        void load();
        onChanged?.();
      } else {
        showToast({ kind: 'err', msg: res.error || 'No se pudo actualizar el aviso.' });
      }
    } catch {
      showToast({ kind: 'err', msg: 'No se pudo actualizar el aviso.' });
    } finally {
      setBusy((b) => ({ ...b, [r.id]: false }));
    }
  };

  const sendAnswer = (r: InboxRow) => {
    const t = answer.trim();
    if (!t) return;
    const body: Record<string, unknown> = { answer: t };
    let okMsg = 'Respuesta guardada';
    if (saveAsDirective) {
      // Si el aviso es de una empresa, la directiva va a esa empresa; si es global, a la empresa elegida arriba.
      const target = r.seller_id ?? sellerId;
      body.save_as_directive =
        directiveScope === 'global' ? { scope: 'global' } : { scope: 'seller', seller_id: target };
      okMsg = 'Respuesta guardada y agregada a las directivas';
    }
    void send(r, body, okMsg);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1 bg-[var(--color-bg-secondary)] p-1 rounded-lg w-fit">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                filter === f.key
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => void load()}
          title="Actualizar"
          className="p-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {toast && (
        <div
          className={`rounded-lg p-3 text-sm ${
            toast.kind === 'ok'
              ? 'bg-[var(--color-success-bg,rgba(34,197,94,0.1))] text-[var(--color-success)]'
              : 'bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {error && (
        <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]">
          {error}
        </div>
      )}

      {loading && rows.length === 0 && !error ? (
        <div className="flex items-center justify-center py-12 text-[var(--color-text-secondary)]">
          <Loader2 size={18} className="animate-spin mr-2" /> Cargando…
        </div>
      ) : rows.length === 0 && !error ? (
        <div className="text-center py-8 text-sm text-[var(--color-text-secondary)]">
          {filter === 'unread' ? 'No hay nada sin leer.' : 'No hay avisos en esta vista.'}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const open = !!expanded[r.id];
            const st = STATUS_TEXT[r.status] ?? { label: r.status, tone: 'info' as const };
            const replying = replyId === r.id;
            return (
              <li
                key={r.id}
                className={`rounded-lg border bg-[var(--color-bg-card)] p-3 space-y-2 ${
                  r.status === 'unread' ? 'border-[var(--color-primary)]' : 'border-[var(--color-border)]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setExpanded((e) => ({ ...e, [r.id]: !open }))}
                  className="w-full flex items-start gap-2 text-left"
                >
                  {open ? (
                    <ChevronDown size={16} className="mt-0.5 shrink-0 text-[var(--color-text-muted)]" />
                  ) : (
                    <ChevronRight size={16} className="mt-0.5 shrink-0 text-[var(--color-text-muted)]" />
                  )}
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--color-text-secondary)]">
                      <span className="px-2 py-0.5 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)]">
                        {INBOX_KIND_LABEL[r.kind] ?? r.kind}
                      </span>
                      <span className="font-medium text-[var(--color-text)]">
                        {r.seller_id === null ? 'Todas las empresas' : sellerName(r.seller_id)}
                      </span>
                      <span>·</span>
                      <span>{fmtDateTime(r.created_at)}</span>
                      <StatusBadge status={st.tone} label={st.label} size="sm" />
                    </div>
                    <div
                      className={`text-sm break-words text-[var(--color-text)] ${
                        r.status === 'unread' ? 'font-semibold' : ''
                      }`}
                    >
                      {r.title}
                    </div>
                  </div>
                </button>

                {open && (
                  <div className="pl-6 space-y-2">
                    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-3 text-xs text-[var(--color-text)] break-words max-h-[45vh] overflow-y-auto">
                      {r.body ? <AssistantMarkdown text={r.body} /> : '(sin texto)'}
                    </div>
                    {r.answer && (
                      <div className="text-xs space-y-1">
                        <div className="text-[var(--color-text-secondary)]">
                          Respuesta{r.answered_by ? ` de ${r.answered_by}` : ''}
                          {r.answered_at ? ` · ${fmtDateTime(r.answered_at)}` : ''}
                        </div>
                        <div className="text-[var(--color-text)] whitespace-pre-wrap break-words">{r.answer}</div>
                      </div>
                    )}
                  </div>
                )}

                <Can cap="assistant.manage">
                  <div className="pl-6 space-y-2">
                    {!replying && (
                      <div className="flex flex-wrap gap-2">
                        {r.status === 'unread' && (
                          <button
                            type="button"
                            onClick={() => void send(r, { mark_read: true }, 'Marcado como leído')}
                            disabled={!!busy[r.id]}
                            className={btnGhost}
                          >
                            {busy[r.id] ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                            Marcar leído
                          </button>
                        )}
                        <button type="button" onClick={() => openReply(r)} disabled={!!busy[r.id]} className={btnGhost}>
                          <Reply size={12} /> {r.answer ? 'Responder de nuevo' : 'Responder'}
                        </button>
                      </div>
                    )}
                    {replying && (
                      <div className="space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-3">
                        <textarea
                          value={answer}
                          onChange={(e) => setAnswer(e.target.value)}
                          maxLength={MAX_TEXT}
                          rows={3}
                          placeholder="Escribí la respuesta…"
                          className={`${inputCls} w-full resize-y`}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="flex items-center gap-2 text-xs text-[var(--color-text)]">
                            <input
                              type="checkbox"
                              checked={saveAsDirective}
                              onChange={(e) => setSaveAsDirective(e.target.checked)}
                            />
                            Guardar también como directiva
                          </label>
                          {saveAsDirective && (
                            <select
                              value={directiveScope}
                              onChange={(e) => setDirectiveScope(e.target.value as 'global' | 'seller')}
                              className={inputCls}
                              title="A quién aplica la directiva"
                            >
                              <option value="seller">Solo {sellerName(r.seller_id ?? sellerId)}</option>
                              {seesAll && <option value="global">Global (todas las empresas)</option>}
                            </select>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => sendAnswer(r)}
                            disabled={!!busy[r.id] || !answer.trim()}
                            className={btnPrimary}
                          >
                            {busy[r.id] && <Loader2 size={12} className="animate-spin" />}
                            Enviar respuesta
                          </button>
                          <button type="button" onClick={() => setReplyId(null)} className={btnGhost}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </Can>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
